import React, {useRef} from "react";
import {Box, Grid, Typography} from "@material-ui/core";
import {HorizontalBar} from "react-chartjs-2";

import BarChart from '@user-components/BarChart';

// 불안정 계산: 밴드별 정상~최대 범위 내 백분율의 평균
// 정상 이하=0%, 최대 이상=100%
const UNSTABLE_REF = {
    0: { normal: 10, max: 25 },     // δ
    1: { normal: 8.6, max: 21.5 },  // θ
    6: { normal: 0.7, max: 1.75 },  // βh
    7: { normal: 0.6, max: 1.5 }    // γ
}

const calcUnstable = (channelData) => {
    let total = 0
    const keys = Object.keys(UNSTABLE_REF)
    keys.forEach((idx) => {
        const val = parseFloat(channelData[idx])
        const { normal, max } = UNSTABLE_REF[idx]
        let pct = 0
        if (val <= normal) {
            pct = 0
        } else {
            pct = ((val - normal) / (max - normal)) * 100
        }
        total += pct
    })
    const avg = total / keys.length
    return parseFloat(Math.min(avg, 95).toFixed(2))
}

export default function BrainBalanceBand(props) {
    const { bandRangeData, height, width, fontSize } = props
    const barChartRef = useRef()

    const defaultData = [];
    defaultData.push(bandRangeData.ch1);
    defaultData.push(bandRangeData.ch2);

    const maxValue = Math.max(
        ...bandRangeData.ch1.map(v => parseFloat(v)),
        ...bandRangeData.ch2.map(v => parseFloat(v))
    )

    // 불안정 데이터 (좌뇌/우뇌)
    const unstableL = calcUnstable(bandRangeData.ch1)
    const unstableR = calcUnstable(bandRangeData.ch2)

    const getLevel = (value) => {
        if (value < 25) return { text: '안정', color: '#4CAF50', desc: '불안정 요소가 낮은 안정적인 상태입니다.' }
        if (value < 50) return { text: '경미', color: '#FFC107', desc: '경미한 불안정이 감지됩니다. 지속적인 관찰이 필요합니다.' }
        if (value < 75) return { text: '주의', color: '#FF9800', desc: '불안정 수준이 높습니다.' }
        return { text: '심각', color: '#F44336', desc: '불안정 수준이 매우 높습니다. 적극적인 뇌기능 훈련이 필요합니다.' }
    }
    const levelL = getLevel(unstableL)
    const levelR = getLevel(unstableR)

    return <Box mt="2" style={{
        width,
        height
    }}>
        <BarChart 
            ref={ barChartRef }
            data={ new Array(6).fill(0) }
            defaultData={defaultData}
            showTopValue={true}
            showTopValueTitle={true}
            topValueMargin={30}
            labels={['δ','θ','α','SMR','β', 'βM', 'βh']}
            height={height ? height : 150}
            width={width}
            fontSize={fontSize ? fontSize : 20}
            topValueFontSize={fontSize ? fontSize : 20}
            suggestedMax={maxValue + 10}
            grouped={true}
            groups={2}
            useColorIndex={true}
            showYAxes={true}
            colors={['#60C3F7','#A9D8AA']}
        />

        <Box mt={4}>
            <Typography variant={'subtitle1'} style={{fontWeight: 'bold', paddingLeft: 15, marginBottom: 10}}>
                불안정 상태
            </Typography>

            {/* 구간 색상 범례 */}
            <Box display={'flex'} mb={1} ml={2}>
                {[
                    { label: '안정', color: '#4CAF50' },
                    { label: '경미', color: '#FFC107' },
                    { label: '주의', color: '#FF9800' },
                    { label: '심각', color: '#F44336' },
                ].map((item, i) => (
                    <Box key={i} display={'flex'} alignItems={'center'} mr={2}>
                        <Box style={{width: 12, height: 12, backgroundColor: item.color, borderRadius: 2, marginRight: 4}} />
                        <Typography variant={'caption'} style={{color: '#555'}}>{item.label}</Typography>
                    </Box>
                ))}
            </Box>

            {/* 좌뇌 / 우뇌 하나의 그래프 */}
            <Box style={{height: 120, position: 'relative'}}>
                <HorizontalBar
                    data={{
                        labels: ['좌뇌', '우뇌'],
                        datasets: [{
                            data: [unstableL, unstableR],
                            backgroundColor: [levelL.color, levelR.color],
                            borderColor: [levelL.color, levelR.color],
                            borderWidth: 1,
                        }]
                    }}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        legend: { display: false },
                        tooltips: {
                            callbacks: {
                                label: function (tooltipItem) {
                                    return tooltipItem.xLabel + '%'
                                }
                            }
                        },
                        animation: {
                            onComplete: function () {
                                const chart = this;
                                const ctx = chart.ctx;
                                const fs = fontSize ? fontSize : 14;
                                ctx.font = 'bold ' + fs + 'px sans-serif';
                                ctx.fillStyle = '#333';
                                ctx.textAlign = 'left';
                                ctx.textBaseline = 'middle';
                                chart.data.datasets.forEach(function (dataset, di) {
                                    const meta = chart.controller.getDatasetMeta(di);
                                    meta.data.forEach(function (bar, index) {
                                        const value = dataset.data[index];
                                        ctx.fillText(value + '%', bar._model.x + 8, bar._model.y);
                                    });
                                });

                                // 구간 배경 색상 표시
                                const xAxis = chart.scales['x-axis-0'];
                                const yAxis = chart.scales['y-axis-0'];
                                const top = yAxis.top;
                                const bottom = yAxis.bottom;
                                const zones = [
                                    { from: 0, to: 25, color: 'rgba(76,175,80,0.08)' },
                                    { from: 25, to: 50, color: 'rgba(255,193,7,0.08)' },
                                    { from: 50, to: 75, color: 'rgba(255,152,0,0.10)' },
                                    { from: 75, to: 100, color: 'rgba(244,67,54,0.10)' },
                                ];
                                zones.forEach(function(zone) {
                                    const left = xAxis.getPixelForValue(zone.from);
                                    const right = xAxis.getPixelForValue(zone.to);
                                    ctx.fillStyle = zone.color;
                                    ctx.fillRect(left, top, right - left, bottom - top);
                                });
                            }
                        },
                        scales: {
                            xAxes: [{
                                display: true,
                                gridLines: {
                                    display: true,
                                    color: 'rgba(0,0,0,0.08)',
                                },
                                ticks: {
                                    fontColor: '#666',
                                    fontSize: fontSize ? fontSize : 12,
                                    min: 0,
                                    max: 100,
                                    stepSize: 25,
                                    callback: function (value) {
                                        return value + '%'
                                    }
                                },
                            }],
                            yAxes: [{
                                display: true,
                                barPercentage: 0.8,
                                categoryPercentage: 0.8,
                                ticks: {
                                    fontColor: '#333',
                                    fontSize: fontSize ? fontSize : 14,
                                    fontStyle: 'bold',
                                },
                                gridLines: {
                                    display: false,
                                },
                            }]
                        }
                    }}
                />
            </Box>

            {/* 설명 박스 */}
            <Box mt={2} p={2} style={{backgroundColor: '#f9f9f9', borderRadius: 8}}>
                {[
                    { label: '좌뇌', level: levelL },
                    { label: '우뇌', level: levelR },
                ].map((item, i) => (
                    <Box key={i} mb={i === 0 ? 1 : 0}>
                        <Typography variant={'body2'} style={{color: '#333', fontWeight: 'bold', display: 'inline'}}>
                            {item.label}
                        </Typography>
                        <Typography variant={'body2'} style={{color: item.level.color, fontWeight: 'bold', display: 'inline', marginLeft: 8}}>
                            [{item.level.text}]
                        </Typography>
                        <Typography variant={'body2'} style={{color: '#666', display: 'inline', marginLeft: 8}}>
                            {item.level.desc}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
}
